import { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Collapse, makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../../../textBoxWithTooltip';
import ActionButton from '../../../actionButton';
import VirtualizedList from '../../../virtualizedList';
import UserCountBadge from '../../../userCountBadge';
import DotsMenu from '../../../dotsMenu';
import Member from './member';
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit-dark.svg';
import { ReactComponent as DeleteIcon } from '../../../../../assets/icons/delete-outlined.svg';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';

const ROW_HEIGHT = 48;
const MAX_VISIBLE_USERS = 5;
const GAP = 12;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish6}`,
    borderRadius: 8,
    maxWidth: 434,
    width: '100%',
  },
  header: {
    boxSizing: 'border-box',
    padding: spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
  },
  userCount: {
    backgroundColor: 'unset',
    padding: spacing(1),
    flexShrink: 0,
  },
  action: {
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
  content: {
    padding: spacing(1, 4, 4, 4),
  },
  listViewport: {
    maxHeight: ROW_HEIGHT * MAX_VISIBLE_USERS + (MAX_VISIBLE_USERS - 1) * GAP,
  },
  scrollY: {
    right: -11,
  },
}));

const ScopeGroup = ({
  className = undefined,
  translations = {},
  group,
  isFirstGroup,
  hasActions = false,
  canAccessUserProfileCheck,
  onGoToUserProfile,
  onEdit = () => {},
  onDelete = () => {},
}) => {
  const [isExpanded, setIsExpanded] = useState(isFirstGroup);

  const classes = useStyles();

  const hasMembers = !!group?.members?.length;

  const toggleExpand = useCallback(() => {
    if (hasMembers) {
      setIsExpanded(prev => !prev);
    }
  }, [hasMembers]);

  const estimateSize = useCallback(() => ROW_HEIGHT, []);

  const handleEdit = useCallback(() => onEdit(group), [group, onEdit]);

  const handleDelete = useCallback(() => onDelete(group), [group, onDelete]);

  const menuItems = useMemo(() => {
    if (!hasActions) return [];
    return [
      {
        type: 'edit',
        label: translations.edit,
        shouldItemRender: () => true,
        action: handleEdit,
        icon: EditIcon,
      },
      {
        type: 'delete',
        label: translations.delete,
        shouldItemRender: () => true,
        action: handleDelete,
        icon: DeleteIcon,
      },
    ];
  }, [translations, hasActions, handleEdit, handleDelete]);

  const renderMember = useCallback(
    member => {
      return (
        <Member
          member={member}
          canAccessUserProfileCheck={canAccessUserProfileCheck}
          onGoToUserProfile={onGoToUserProfile}
        />
      );
    },
    [canAccessUserProfileCheck, onGoToUserProfile]
  );

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.header} onClick={toggleExpand}>
        <TextBoxWithTooltip text={group.name} variant="subtitle1" />
        <UserCountBadge
          className={classes.userCount}
          userCount={group?.members?.length ?? 0}
        />
        {hasActions && (
          <DotsMenu className={classes.action} menuItems={menuItems} />
        )}
        <ActionButton
          className={classes.action}
          iconClass={classNames(classes.expandIcon, {
            [classes.expandIconOpened]: isExpanded,
          })}
          type={ACTION_BUTTON_TYPES.ARROW}
          isDisabled={!group?.members?.length}
        />
      </div>
      <Collapse in={isExpanded && hasMembers} timeout="auto" unmountOnExit>
        <div className={classes.content}>
          <VirtualizedList
            verticalScrollClass={classes.scrollY}
            viewportClass={classes.listViewport}
            items={group?.members || []}
            count={group?.members?.length ?? 0}
            gap={GAP}
            estimateSize={estimateSize}
            renderItem={renderMember}
          />
        </div>
      </Collapse>
    </div>
  );
};

ScopeGroup.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object,
  group: PropTypes.object.isRequired,
  hasActions: PropTypes.bool,
  isFirstGroup: PropTypes.bool.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

export default ScopeGroup;
